export const RACES = [
  { id: 'HUMAN', name: 'Human' },
  { id: 'ELF', name: 'Elf' },
  { id: 'DWARF', name: 'Dwarf' },
  { id: 'ORC', name: 'Orc' },
  { id: 'HALFLING', name: 'Halfling' },
  { id: 'UNDEAD', name: 'Undead' },
  { id: 'AVIAN', name: 'Avian' },
  { id: 'FAERY', name: 'Faery' },
] as const;

export const PERSONALITIES = [
  { id: 'MERCHANT', name: 'Merchant' },
  { id: 'SAGE', name: 'Sage' },
  { id: 'ROGUE', name: 'Rogue' },
  { id: 'MYSTIC', name: 'Mystic' },
  { id: 'WARRIOR', name: 'Warrior' },
  { id: 'TACTICIAN', name: 'Tactician' },
  { id: 'CLERIC', name: 'Cleric' },
  { id: 'WAR_HERO', name: 'War Hero' },
  { id: 'SHEPHERD', name: 'Shepherd' }, // Previous personality, kept for backwards compatibility
] as const;

export const GENDERS = [
  { id: 'MALE', name: 'Male' },
  { id: 'FEMALE', name: 'Female' },
] as const;

// Race-specific unit names for offense specialists, defense specialists, and elites
// Names match Race.java definitions in the backend
export const RACE_UNITS: Record<string, { offense: string; defense: string; elite: string }> = {
  HUMAN: { offense: 'Swordsmen', defense: 'Archers', elite: 'Knights' },
  ELF: { offense: 'Archers', defense: 'Rangers', elite: 'Elf Lords' },
  DWARF: { offense: 'Warriors', defense: 'Axemen', elite: 'Berserkers' },
  ORC: { offense: 'Goblins', defense: 'Trolls', elite: 'Ogres' },
  HALFLING: { offense: 'Strongarms', defense: 'Slingers', elite: 'Brutes' },
  UNDEAD: { offense: 'Skeletons', defense: 'Zombies', elite: 'Ghouls' },
  AVIAN: { offense: 'Griffins', defense: 'Harpies', elite: 'Drakes' },
  FAERY: { offense: 'Magicians', defense: 'Druids', elite: 'Beastmasters' },
};

// Race-specific unit offense/defense points (matching InMemoryPropertySystem.java)
export const RACE_UNIT_STATS: Record<string, {
  soldier: { off: number; def: number };
  offense: { off: number; def: number };
  defense: { off: number; def: number };
  elite: { off: number; def: number };
}> = {
  HUMAN: { soldier: { off: 1, def: 1 }, offense: { off: 4, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 8, def: 5 } },
  ELF: { soldier: { off: 1, def: 1 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 6, def: 4 } },
  DWARF: { soldier: { off: 1, def: 1 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 7, def: 3 } },
  ORC: { soldier: { off: 1, def: 1 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 9, def: 2 } },
  HALFLING: { soldier: { off: 2, def: 2 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 6, def: 5 } },
  UNDEAD: { soldier: { off: 1, def: 1 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 9, def: 3 } },
  AVIAN: { soldier: { off: 1, def: 1 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 8, def: 2 } },
  FAERY: { soldier: { off: 1, def: 1 }, offense: { off: 5, def: 0 }, defense: { off: 0, def: 5 }, elite: { off: 4, def: 8 } },
};

// Get "(off/def)" points label for a unit type and race
export function getUnitPointsLabel(race: string | undefined, unitType: 'soldier' | 'offense' | 'defense' | 'elite'): string {
  const stats = race ? RACE_UNIT_STATS[race.toUpperCase()] : undefined;
  if (!stats) {
    // Fallback defaults
    switch (unitType) {
      case 'soldier': return '(1/1)';
      case 'offense': return '(5/0)';
      case 'defense': return '(0/5)';
      case 'elite': return '(8/2)';
    }
  }
  const s = stats[unitType];
  return `(${s.off}/${s.def})`;
}

// Get race-specific unit name with fallback
export function getRaceUnitName(race: string | undefined, unitType: 'offense' | 'defense' | 'elite'): string {
  const raceUnits = race ? RACE_UNITS[race.toUpperCase()] : undefined;
  if (raceUnits) {
    return raceUnits[unitType];
  }
  // Fallback to generic names
  switch (unitType) {
    case 'offense': return 'Off. Specialists';
    case 'defense': return 'Def. Specialists';
    case 'elite': return 'Elites';
  }
}

export type Race = typeof RACES[number]['id'];
export type Personality = typeof PERSONALITIES[number]['id'];
export type Gender = typeof GENDERS[number]['id'];

// Get race display name from ID (HUMAN → Human)
export function getRaceDisplayName(raceId: string): string {
  const race = RACES.find(r => r.id === raceId.toUpperCase());
  return race?.name ?? raceId;
}

// Get personality display name from ID (WARRIOR → Warrior)
export function getPersonalityDisplayName(personalityId: string): string {
  const personality = PERSONALITIES.find(p => p.id === personalityId.toUpperCase());
  return personality?.name ?? personalityId;
}

// Convert honor title from UPPERCASE to Title Case (KNIGHT → Knight)
export function formatHonorTitle(title: string): string {
  if (!title) return title;
  return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
}

// Personality title patterns from the Utopian Encyclopedia
// Some are prefixes (The Wealthy [Name]), some are suffixes ([Name] the Warrior)
const PERSONALITY_TITLES: Record<string, { prefix?: string; suffix?: string }> = {
  MERCHANT: { prefix: 'The Wealthy' },
  SAGE: { prefix: 'The Wise' },
  ROGUE: { suffix: 'the Rogue' },
  MYSTIC: { suffix: 'the Sorcerer' },
  WARRIOR: { suffix: 'the Warrior' },
  TACTICIAN: { prefix: 'The Conniving' },
  CLERIC: { suffix: 'the Blessed' },
  WAR_HERO: { suffix: 'the Hero' },
  SHEPHERD: { prefix: 'The Humble' },
};

// Format full ruler name with personality title and honor
// Example: "Knight Lord Testing the Warrior" (for a Warrior with honor title Knight)
export function formatRulerWithTitle(
  rulerName: string,
  personality: string,
  honorTitle: string
): string {
  const titlePattern = PERSONALITY_TITLES[personality.toUpperCase()];
  const formattedHonorTitle = formatHonorTitle(honorTitle);

  // Build the base name with honor title
  const nameWithHonor = `${formattedHonorTitle} ${rulerName}`;

  if (!titlePattern) {
    return nameWithHonor;
  }

  if (titlePattern.prefix) {
    return `${titlePattern.prefix} ${nameWithHonor}`;
  }

  if (titlePattern.suffix) {
    return `${nameWithHonor} ${titlePattern.suffix}`;
  }

  return nameWithHonor;
}
